import React, { useEffect, useState } from 'react';
import { MapPin, Search } from 'lucide-react';
import { states } from '../../data/locations/states';
import { cities } from '../../data/locations/cities';
import { getAreasForCity } from '../../data/locations/areas';

interface LocationSelectorProps {
  state: string;
  city: string;
  area: string;
  onStateChange: (value: string) => void;
  onCityChange: (value: string) => void;
  onAreaChange: (value: string) => void;
  hideState?: boolean;
}

export default function LocationSelector({
  state,
  city,
  area,
  onStateChange,
  onCityChange,
  onAreaChange,
  hideState = false
}: LocationSelectorProps) {
  const [availableCities, setAvailableCities] = useState<Array<{ value: string; label: string }>>([]);
  const [availableAreas, setAvailableAreas] = useState<Array<{ value: string; label: string; pincode?: string }>>([]);
  const [areaSearch, setAreaSearch] = useState('');
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);

  useEffect(() => {
    if (state) {
      // Get cities for the current state
      const stateCities = cities[state] || [];
      setAvailableCities(stateCities);
      
      // Reset city and area when state changes
      if (!hideState) {
        onCityChange('');
        onAreaChange('');
      }
    } else {
      setAvailableCities([]);
    }
  }, [state, hideState, onCityChange, onAreaChange]);

  useEffect(() => {
    if (city) {
      const areas = getAreasForCity(city);
      setAvailableAreas(areas);
      // Reset area when city changes
      onAreaChange('');
      setAreaSearch('');
    } else {
      setAvailableAreas([]);
    }
  }, [city, onAreaChange]);

  const filteredAreas = availableAreas.filter(areaOption => 
    areaOption.label.toLowerCase().includes(areaSearch.toLowerCase()) ||
    areaOption.value.toLowerCase().includes(areaSearch.toLowerCase()) ||
    (areaOption.pincode && areaOption.pincode.includes(areaSearch))
  );

  const handleAreaSelect = (selectedArea: { value: string; label: string }) => {
    onAreaChange(selectedArea.value);
    setAreaSearch(selectedArea.label);
    setShowAreaDropdown(false);
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        <MapPin className="inline-block w-4 h-4 mr-2" />
        Location Details
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {!hideState && (
          <div>
            <select
              value={state}
              onChange={(e) => onStateChange(e.target.value)}
              required
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-md"
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className={hideState ? 'col-span-2' : ''}>
          <select
            value={city}
            onChange={(e) => onCityChange(e.target.value)}
            required
            disabled={!state}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-md disabled:bg-gray-100"
          >
            <option value="">Select City</option>
            {availableCities.map((city) => (
              <option key={city.value} value={city.value}>
                {city.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="relative">
        <div className="relative">
          <input
            type="text"
            value={areaSearch}
            onChange={(e) => {
              setAreaSearch(e.target.value);
              setShowAreaDropdown(true);
            }}
            onFocus={() => setShowAreaDropdown(true)}
            placeholder="Search area/locality..."
            disabled={!city}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>

        {showAreaDropdown && city && (
          <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
            {filteredAreas.length > 0 ? (
              <ul className="py-1">
                {filteredAreas.map((areaOption) => (
                  <li key={areaOption.value}>
                    <button
                      type="button"
                      onClick={() => handleAreaSelect(areaOption)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                    >
                      <div className="font-medium text-gray-900">{areaOption.label}</div>
                      {areaOption.pincode && (
                        <div className="text-sm text-gray-500">Pincode: {areaOption.pincode}</div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500">
                No areas found matching your search
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}